import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  createSubmission,
  getRecord,
  getRecordTypeSchema,
  linkRecord,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  listHouseholdPerformers,
  type HouseholdPerformer,
  type LinkRow,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import { useRecordDetail } from '../lib/useRecordDetail';
import { openRecordEditor, useRecordsVersion, bumpRecordsVersion } from '../lib/recordEditor';
import { SUBMISSION_LINK } from '../lib/submissions';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { fmtMoney, humanizeValue } from '../lib/fieldDisplay';
import {
  AA,
  LINK,
  colourOrderFrom,
  currentSides,
  engagementSeasonLine,
  otherEnd,
  seamBlocks,
  seasonEpisode,
  sidesLabel,
  sidesTrail,
  type SeamBlock,
} from '../lib/auditionSpread';
import { departmentLabel, expectsAudition, expectsSides } from '../lib/discipline';
import './spread.css';

// A4 — THE ROLE. *Everything about this part.*
//
// THIS IS THE SURFACE THAT HAS TO BE EXCELLENT, and the two decisions that
// created the record both say so in as many words.
//
//   fdf126f6: "The Role screen becomes the surface that must be excellent —
//   it is where 'everything about this part' is answered."
//
//   15155d2f: booking creates a seam. The tape, sides, casting contact and
//   coach notes sit on a record that becomes historical, while call sheets,
//   shoot days, contract and payments accrue on a NEW one — though the
//   actor's mental model is continuous. "The Role screen, not the audition,
//   is therefore the surface that has to be excellent."
//
// So the body of this screen is ONE CHRONOLOGY PER PERFORMER, auditions and
// engagements together, with the seam drawn across it rather than splitting
// it into two lists. Drawing the seam is honest — a booking is a real change
// of kind. Splitting the list would be the thing the Role exists to prevent.
//
// ===================================================================
// WHY THERE IS A BLOCK PER PERFORMER AND NO SUBJECT ON THE ROLE
// ===================================================================
// aa_role has NO subject, and that absence is load-bearing: two siblings
// reading for the same character read ONE role record, ONE sides trail, and
// file TWO auditions. A subject here would force a second role record for the
// same character and split the trail.
//
// It also has NO lifecycle, for the same reason — a role's state is "where
// has this gone", and the answer differs per performer. So there is no
// stepper on this screen. Each performer's furthest state is shown inside
// their own block, which is the only place the question has one answer.
//
// A ROLE WITH ONE PERFORMER RENDERS NO HEADING FOR THEM. Same rule the
// household Today follows: the subject is part of the item, never a heading,
// or the subject switcher the household home exists to avoid grows back here.

export interface RoleSurfaceProps {
  tenantId: string;
  id: string;
  onOpenProject: (projectId: string) => void;
  onOpenAudition: (auditionId: string) => void;
  /** A5 — the far side of the seam. Until this existed the seam row called
   * openRecordEditor: the schema-driven FALLBACK door used as the front one,
   * which is precisely the defect A4 was written to fix for auditions, left
   * standing one record over. */
  onOpenEngagement: (engagementId: string) => void;
  onBackToProjects: () => void;
  // B20a §3 — "Submit for this" on an AUDITION-LESS part: the household
  // already knows the role and its project, so the draft is created
  // pre-linked to both, rather than routed through the paste-a-link sheet
  // that exists for the case where nothing is known yet.
  onOpenSubmission: (submissionId: string) => void;
}

interface RoleAround {
  /** See AuditionSpread's own note: an absence is claimed only after looking.
   * "Nobody has read for this part yet" is a real statement about a
   * household, and it must never be made about a request still in flight. */
  loaded: boolean;
  project: RecordRow | null;
  sides: RecordRow[];
  sidesSchema: RecordTypeSchema | null;
  blocks: SeamBlock[];
  performers: Map<string, HouseholdPerformer>;
  /** This account's own performer, when one has been claimed — read directly
   * (an account is one career, so there is at most one) rather than being
   * told which one by a scope on the path. Used only to pre-attach a newly
   * filed read or booking to it. */
  mySubjectId: string | null;
  /** Records the server would not answer link data for — never silently
   * dropped. */
  linksMissing: number;
}

const EMPTY: RoleAround = {
  loaded: false,
  project: null,
  sides: [],
  sidesSchema: null,
  blocks: [],
  performers: new Map(),
  mySubjectId: null,
  linksMissing: 0,
};

function when(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function RoleSurface({
  tenantId,
  id,
  onOpenProject,
  onOpenAudition,
  onOpenEngagement,
  onBackToProjects,
  onOpenSubmission,
}: RoleSurfaceProps) {
  const { record, links, error, reload } = useRecordDetail(tenantId, AA.ROLE, id);
  const [around, setAround] = useState<RoleAround>(EMPTY);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [submittingForRole, setSubmittingForRole] = useState(false);
  const recordsVersion = useRecordsVersion();

  const projectId = record ? otherEnd(links, record.id, LINK.ROLE_PROJECT) : null;

  useEffect(() => {
    if (!record) return;
    let cancelled = false;
    setAround((prev) => ({ ...prev, loaded: false }));
    async function load() {
      try {
        const [project, sidesSchema, allSides, auds, engs, performers, subjects] = await Promise.all([
          projectId
            ? getRecord(tenantId, projectId).catch((err) => {
                if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
                throw err;
              })
            : Promise.resolve(null),
          getRecordTypeSchema(tenantId, AA.SIDES).catch(() => null),
          listAllRecordsWithLinks(tenantId, AA.SIDES, { linkTypes: [LINK.SIDES_ROLE] }),
          listAllRecordsWithLinks(tenantId, AA.AUDITION, {
            linkTypes: [LINK.AUDITION_ROLE, LINK.AUDITION_SUBJECT],
          }),
          listAllRecordsWithLinks(tenantId, AA.ENGAGEMENT, {
            linkTypes: [LINK.ENGAGEMENT_ROLE, LINK.ENGAGEMENT_SUBJECT],
          }),
          // A CHILD IS REFUSED THIS READ AND THAT IS THE DESIGN, not an
          // error: aa_subject is min_role_read 'staff'. Her own blocks still
          // render; they simply carry no borrowed display name. The same
          // reasoning HouseholdHome states at length for its own 403.
          listHouseholdPerformers(tenantId).catch(() => [] as HouseholdPerformer[]),
          // This account's own performer — an account is one career, so
          // there is at most one — read the same way, for the same 403.
          listAllRecordsByType(tenantId, AA.SUBJECT).catch(() => [] as RecordRow[]),
        ]);
        if (cancelled) return;

        const sides = activeOnly(allSides.records).filter(
          (r) => otherEnd(allSides.linksByRecordId[r.id] ?? [], r.id, LINK.SIDES_ROLE) === id
        );

        /** The rows of one type that hang off THIS role, each paired with the
         * performer it is about. Both hops are ordinary link reads; nothing
         * here knows whether the caller is scoped — that was decided by the
         * read above, in the one place that decides it. */
        const mine = (
          rows: RecordRow[],
          linksBy: Record<string, LinkRow[]>,
          roleLink: string,
          subjectLink: string
        ): { record: RecordRow; subjectId: string | null }[] =>
          activeOnly(rows)
            .filter((r) => otherEnd(linksBy[r.id] ?? [], r.id, roleLink) === id)
            .map((r) => ({
              record: r,
              subjectId: otherEnd(linksBy[r.id] ?? [], r.id, subjectLink),
            }));

        const blocks = seamBlocks(
          mine(auds.records, auds.linksByRecordId, LINK.AUDITION_ROLE, LINK.AUDITION_SUBJECT),
          mine(engs.records, engs.linksByRecordId, LINK.ENGAGEMENT_ROLE, LINK.ENGAGEMENT_SUBJECT)
        );

        setAround({
          loaded: true,
          project,
          sides,
          sidesSchema,
          blocks,
          performers: new Map(performers.map((p) => [p.id, p])),
          mySubjectId: activeOnly(subjects)[0]?.id ?? null,
          linksMissing: auds.unreadable.length + engs.unreadable.length,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, record?.id, projectId, recordsVersion]);

  async function submitForThisRole(): Promise<void> {
    if (!record) return;
    setSubmittingForRole(true);
    setFailure(null);
    try {
      const submission = await createSubmission(tenantId, { opportunity: record.display_name });
      await linkRecord(tenantId, submission.id, SUBMISSION_LINK.ROLE, record.id);
      if (projectId) await linkRecord(tenantId, submission.id, SUBMISSION_LINK.PROJECT, projectId);
      if (around.mySubjectId) await linkRecord(tenantId, submission.id, SUBMISSION_LINK.SUBJECT, around.mySubjectId);
      bumpRecordsVersion();
      onOpenSubmission(submission.id);
    } catch (err) {
      setFailure(describeReadFailure(err));
    } finally {
      setSubmittingForRole(false);
    }
  }

  if (error) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Role</div>
        <div class="sp__say">
          {error.headline}
          <br />
          <span class="sp__say-soft">{error.detail}</span>
        </div>
        {error.retryable ? (
          <button type="button" class="sp__retry" onClick={reload}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!record) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Role</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const colourOrder = colourOrderFrom(around.sidesSchema);
  const trail = sidesTrail(around.sides, colourOrder);
  const head = currentSides(trail, colourOrder);
  const size = record.fields.size;
  // ===================================================================
  // A9 — WHAT KIND OF JOB THIS IS, asked once (decision 3e76a155).
  //
  // Every consequence below reads one of these three. Nothing on this screen
  // tests `discipline === 'crew'` itself — see lib/discipline.ts's header for
  // why that rule exists and what breaks the day somebody ignores it.
  //
  // ⚑ AN ABSENT REGION, NOT AN EMPTY ONE. "No pages issued for this part
  // yet" on a gaffer's job is the product promising something that will never
  // arrive — and "yet" is the word that makes it a promise. A grip does not
  // get sides in six weeks; a grip does not get sides.
  const sidesHere = expectsSides(record);
  const readsHere = expectsAudition(record);
  const department = departmentLabel(record);
  const rate = record.fields.quoted_rate;
  const basis = record.fields.quoted_rate_basis;
  const auditionCount = around.blocks.reduce(
    (n, b) => n + b.entries.filter((e) => e.kind === 'audition').length,
    0
  );
  const engagementCount = around.blocks.reduce(
    (n, b) => n + b.entries.filter((e) => e.kind === 'engagement').length,
    0
  );
  // One performer means no heading — see this file's header.
  const nameBlocks = around.blocks.length > 1;

  return (
    <div class="sp">
      <div class="sp__crumb">
        <button type="button" onClick={onBackToProjects}>
          Projects
        </button>
        <span class="sp__crumb-sep">›</span>
        {around.project ? (
          <>
            <button type="button" onClick={() => onOpenProject(around.project!.id)}>
              {around.project.display_name}
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        ) : null}
        <span class="sp__crumb-cur">{record.display_name}</span>
      </div>

      <div class={`sp__grid${sidesHere ? '' : ' sp__grid--nosides'}`}>
        {/* ---- HERO ---- */}
        <div class="sp__hero">
          <div class="sp__hero-top">
            <div>
              <div class="sp__label">
                {around.project ? <span>{around.project.display_name}</span> : null}
                {typeof size === 'string' && size ? <span class="sp__chip">{humanizeValue(size)}</span> : null}
                {/* The department reads BESIDE the size, not instead of it: a
                    crew job can be a 2nd AC (department) on a small unit
                    (size), and both are things the person would say. */}
                {department ? <span class="sp__chip">{department}</span> : null}
              </div>
              <h3 class="sp__title">{record.display_name}</h3>
              {/* The rate and its basis are rendered TOGETHER and the basis
                  VERBATIM. It is free text on purpose — "per day", "per
                  episode", "weekly on a 3-day guarantee" — and a picklist
                  here would have been wrong within a month. */}
              {typeof rate === 'number' ? (
                <p class="sp__sub">
                  <b>{fmtMoney(rate)}</b>
                  {typeof basis === 'string' && basis.trim() ? <> {basis}</> : null}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              class="sp__edit"
              onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.ROLE, id })}
            >
              Edit
            </button>
          </div>

          {typeof record.fields.character_notes === 'string' && record.fields.character_notes.trim() ? (
            <p class="sp__note">{record.fields.character_notes}</p>
          ) : null}

          {/* NO STEPPER HERE. aa_role declares no lifecycle, deliberately: a
              role's state is "where has this gone" and the answer differs per
              performer. It lives inside each block below, which is the only
              place the question has one answer. */}
        </div>

        {/* ---- SIDES: this column is the trail's HOME, not a reference ----
             …and it is ABSENT on a crew job rather than empty. See sidesHere
             above. The grid drops the column with it — an empty second column
             would be a hole in the layout announcing what is missing. */}
        {!sidesHere ? null : (
        <div class="sp__aside">
          <div class="sp__sec">
            <span>Sides</span>
            <span>{trail.length}</span>
          </div>

          {head ? (
            <>
              <div class="sp__big">{sidesLabel(head)}</div>
              <p class="sp__note">
                {when(head.fields.issued_at) ? <>issued {when(head.fields.issued_at)}</> : null}
                {typeof head.fields.page_count === 'number' ? <> · {head.fields.page_count} pp</> : null}
              </p>
              {typeof head.fields.changed_scenes === 'string' && head.fields.changed_scenes.trim() ? (
                <p class="sp__note">{head.fields.changed_scenes}</p>
              ) : null}
            </>
          ) : around.loaded ? (
            // A statement of fact, never an instruction. Recording a revision
            // is the button below and a caption explaining it would be a
            // caption admitting the button does not teach itself.
            <div class="sp__empty">No pages issued for this part yet.</div>
          ) : null}

          {trail.length > 0 ? (
            <div class="sp__trail">
              {trail.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  class={`sp__trail-row${head && row.id === head.id ? ' sp__trail-row--head' : ''}`}
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.SIDES, id: row.id })}
                >
                  <span>{sidesLabel(row)}</span>
                  <span class="sp__trail-when">
                    {when(row.fields.issued_at) ?? '—'}
                    {typeof row.fields.page_count === 'number' ? ` · ${row.fields.page_count}pp` : ''}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <div class="sp__acts">
            <button
              type="button"
              class="sp__act"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: AA.SIDES,
                  prefillRels: { [LINK.SIDES_ROLE]: [id] },
                })
              }
            >
              Record a revision
            </button>
            {/* B20a §3 — offered only while the part has NO audition anywhere
                (every performer's block, not just one) — the moment one
                exists, this part has already entered the spine the old way. */}
            {around.loaded && !around.blocks.some((b) => b.entries.some((e) => e.kind === 'audition')) ? (
              <button
                type="button"
                class="sp__act"
                data-testid="submit-for-this"
                disabled={submittingForRole}
                onClick={() => void submitForThisRole()}
              >
                Submit for this
              </button>
            ) : null}
          </div>
        </div>
        )}

        {/* ---- THE SEAM ---- */}
        <div class="sp__body">
          {!around.loaded ? null : around.blocks.length === 0 ? (
            <div class="sp__empty">
              {readsHere ? 'Nobody has read for this part yet.' : 'Nobody is attached to this job yet.'}
            </div>
          ) : (
            around.blocks.map((block) => {
              const performer = around.performers.get(block.subjectId);
              const auditions = block.entries.filter((e) => e.kind === 'audition');
              const engagements = block.entries.filter((e) => e.kind === 'engagement');
              return (
                <div key={block.subjectId || 'unassigned'}>
                  {nameBlocks ? (
                    <div class="sp__seam-who">
                      {performer ? performer.name : block.subjectId ? 'A performer' : 'No performer recorded'}
                    </div>
                  ) : null}
                  <div class="sp__seam">
                    {auditions.map((e) => (
                      <button
                        type="button"
                        key={e.record.id}
                        class="sp__seam-item"
                        onClick={() => onOpenAudition(e.record.id)}
                      >
                        <span class="sp__row-top">
                          <span class="sp__row-name">
                            {e.record.lifecycle_state
                              ? humanizeValue(e.record.lifecycle_state)
                              : e.record.display_name}
                          </span>
                          <span class="sp__row-when">{when(e.record.fields.submitted_at) ?? ''}</span>
                        </span>
                        <span class="sp__row-sub">
                          {typeof e.record.fields.modality === 'string' && e.record.fields.modality
                            ? humanizeValue(e.record.fields.modality)
                            : 'Audition'}
                          {seasonEpisode(e.record.fields) ? ` · ${seasonEpisode(e.record.fields)}` : ''}
                          {typeof e.record.fields.outcome === 'string' && e.record.fields.outcome.trim()
                            ? ` — “${e.record.fields.outcome}”`
                            : ''}
                        </span>
                      </button>
                    ))}

                    {/* THE SEAM ITSELF. Drawn because a booking is a real
                        change of kind; kept inside one chronology because the
                        actor's mental model is continuous across it, and
                        spanning that is the whole reason this record exists. */}
                    {engagements.length > 0 ? <div class="sp__seam-line">Booked</div> : null}

                    {engagements.map((e) => (
                      <button
                        type="button"
                        key={e.record.id}
                        class="sp__seam-item sp__seam-item--eng"
                        onClick={() => onOpenEngagement(e.record.id)}
                      >
                        <span class="sp__row-top">
                          <span class="sp__row-name">
                            {e.record.lifecycle_state
                              ? humanizeValue(e.record.lifecycle_state)
                              : e.record.display_name}
                          </span>
                          <span class="sp__row-when">
                            {when(e.record.fields.block_start) ?? when(e.record.fields.offered_at) ?? ''}
                          </span>
                        </span>
                        <span class="sp__row-sub">
                          {[
                            engagementSeasonLine(e.record.fields),
                            typeof e.record.fields.rate === 'number'
                              ? fmtMoney(e.record.fields.rate) +
                                (typeof e.record.fields.rate_basis === 'string' && e.record.fields.rate_basis
                                  ? ` ${e.record.fields.rate_basis}`
                                  : '')
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ---- RAIL: everything, including zeros ---- */}
        <div class="sp__rail">
          {/* THE RAIL SHOWS ZEROS ON PURPOSE — a count of nothing is still an
              answer, and hiding it makes a reader wonder whether the number
              is missing or the thing is. That argument holds for a count that
              CAN be non-zero. On a crew job an audition count is not zero, it
              is not a question, and the pill would be furniture. */}
          {!readsHere ? null : (
            <span class={`sp__pill${auditionCount === 0 ? ' sp__pill--zero' : ''}`}>
              Auditions <span class="sp__pill-n">{auditionCount}</span>
            </span>
          )}
          <span class={`sp__pill${engagementCount === 0 ? ' sp__pill--zero' : ''}`}>
            Engagements <span class="sp__pill-n">{engagementCount}</span>
          </span>
          {!sidesHere ? null : (
            <span class={`sp__pill${trail.length === 0 ? ' sp__pill--zero' : ''}`}>
              Sides <span class="sp__pill-n">{trail.length}</span>
            </span>
          )}
          <span class={`sp__pill${around.blocks.length === 0 ? ' sp__pill--zero' : ''}`}>
            {readsHere ? 'Performers' : 'People'} <span class="sp__pill-n">{around.blocks.length}</span>
          </span>
          {/* ⚑ EVERY ROLE HAS A WAY TO BECOME WORK, AND BOTH SIDES OF THE
              SEAM ARE REACHABLE FROM HERE.

              A read is a performance thing and is offered only there. A
              BOOKING is offered on both, and the A10 walk is why that is
              written down: until it looked, "+ Log the booking" was the pill
              A9 had just added for crew, and it was the ONLY place in the
              entire web that created an engagement. An actor could file a
              read, get the part, and have nowhere to record it — the product
              named for going from the first call to the wrap could not record
              the booking in between.

              Not every booking comes from a read (the pack says so itself:
              `engagement_audition` is optional by design, for the offer that
              arrives without one), so this is not a step in a wizard. It is
              the act, available wherever the work is. */}
          {readsHere ? (
            <button
              type="button"
              class="sp__pill"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: AA.AUDITION,
                  // ⚑ THE PERFORMER COMES WITH IT WHEN THE APP KNOWS WHO.
                  // Filing a read used to produce one attached to nobody when
                  // this account had not yet claimed its own performer record
                  // — which the project spine then either lost or, worse,
                  // counted as a second actor. Before that claim there IS no
                  // answer, and the picker asks; that is a different thing
                  // from throwing one away.
                  prefillRels: {
                    [LINK.AUDITION_ROLE]: [id],
                    ...(around.mySubjectId ? { [LINK.AUDITION_SUBJECT]: [around.mySubjectId] } : {}),
                  },
                })
              }
            >
              + File a read
            </button>
          ) : null}
          <button
            type="button"
            class="sp__pill"
            onClick={() =>
              openRecordEditor({
                mode: 'create',
                typeKey: AA.ENGAGEMENT,
                prefillRels: {
                  [LINK.ENGAGEMENT_ROLE]: [id],
                  ...(around.mySubjectId ? { [LINK.ENGAGEMENT_SUBJECT]: [around.mySubjectId] } : {}),
                },
              })
            }
          >
            + Log the booking
          </button>
        </div>
      </div>

      {/* NEVER SILENTLY SHORT. A record the scoped read returned but the wide
          read could not answer for would otherwise vanish from a chronology
          that claims to be complete. */}
      {around.linksMissing > 0 ? (
        <p class="sp__say-soft" style={{ margin: '12px 2px' }}>
          {around.linksMissing} record{around.linksMissing === 1 ? '' : 's'} could not be placed on
          this {readsHere ? 'part' : 'job'} — their links were not answered for.
        </p>
      ) : null}

      {failure ? (
        <p class="sp__say-soft" style={{ margin: '12px 2px' }}>
          {failure.headline} — {failure.detail}
        </p>
      ) : null}
    </div>
  );
}
